import React, { useState } from "react";
import Button from "components/form/Button";
import Dropdown from "components/form/Dropdown";
import FileUpload from "components/form/FileUpload";
import TextInput from "components/form/TextInput";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  getDataFromLocalStorage,
  getFilenameFromUrl,
  handleFileDownload,
  handleViewPreview,
  objectToFormData,
  titleCaseString,
} from "utils/helpers";
import { icons, paymentAbstractPath } from "utils/constants";
import CreatableCityDropDown from "components/form/CreatableCityDropDown";
import Location from "components/form/Location";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsEditDataOfProfile,
  setIsEditUserProfile,
  throwError,
  throwSuccess,
  UpdateDetailsOfUsers,
} from "store/slices";
import { filter, isEqual, pickBy } from "lodash";

const EditCompanyDetails = ({ profileData, setIsEditDetails }) => {
  const dispatch = useDispatch();
  const { membershipList } = useSelector((state) => ({
    membershipList: state.global.membershipList,
  }));

  const memberList = filter(membershipList, (o) => o.id !== "6");
  const [isLoading, setIsLoading] = useState(false);

  const {
    user_id: profileUserId,
    user_type,
    memberType,
    membership_payment_proof,
    company_details = {},
  } = profileData;

  const userType = getDataFromLocalStorage("user_type");

  const getMemberId = (name) => {
    let returnValue = "";
    const findObj = membershipList.find((o) => o.name === name);
    if (findObj) {
      returnValue = findObj.id;
    }
    return returnValue;
  };
  const {
    user_id: companyUserId,
    company_name,
    company_email_id,
    company_contact_number,
    company_state,
    company_country,
    company_city,
    strength_of_company,
    no_of_premium_professional_members,
  } = company_details;

  const targetUserId = +profileUserId || +companyUserId || "";

  const initialValues = {
    membership_plan_id: getMemberId(memberType),
    membership_payment_proof: membership_payment_proof || "",
    company_name: company_name || "",
    company_email_id: company_email_id || "",
    company_contact_number: company_contact_number || "",
    company_country: company_country || "",
    company_country_name: "",
    company_state: company_state || "",
    company_state_name: "",
    company_city: company_city || "",
    company_city_name: "",
    strength_of_company: strength_of_company || "",
    no_of_premium_professional_members:
      no_of_premium_professional_members || "",
    country_name: "",
    state_name: "",
    other_city: "",
  };

  const membershipPaymentProofRule = Yup.string().when("membership_plan_id", {
    is: (planId) =>
      userType === "6" &&
      planId !== initialValues.membership_plan_id &&
      !["2", "11"].includes(planId),
    then: Yup.string().required("Payment proof is required."),
    otherwise: Yup.string(),
  });

  const companyProfileValidation = {
    company_name: Yup.string().required("Company name is required."),
    company_email_id: Yup.string()
      .required("Email is required.")
      .email("Email must be a valid email"),
    company_contact_number: Yup.string()
      .required("Phone number is required.")
      .min(10, "Phone number must be 10 digit.")
      .max(10, "Phone number must be 10 digit."),
    company_country: Yup.string().required("Country is required."),
    company_state: Yup.string().required("State/Province is required."),
    company_city: Yup.string().required("City is required."),
    other_city: Yup.lazy((_, o) => {
      const { company_city } = o?.parent;
      if (company_city === "586") {
        return Yup.string().required("Please enter Other city name.");
      } else {
        return Yup.string();
      }
    }),
  };

  // A membership-type change is a membership request, not a profile update, so
  // it is validated on its own requirements instead of the company fields.
  // Formik turns empty strings into undefined before validating, so both
  // sides are normalised before they are compared.
  const validationSchema = Yup.lazy((values) =>
    (values?.membership_plan_id || "") !==
    (initialValues.membership_plan_id || "")
      ? Yup.object().shape({
          membership_plan_id: Yup.string().required(
            "Member type is required."
          ),
          membership_payment_proof: membershipPaymentProofRule,
        })
      : Yup.object().shape({
          membership_payment_proof: membershipPaymentProofRule,
          ...companyProfileValidation,
        })
  );

  const handleUpdate = async (updatedFields) => {
    setIsLoading(true);
    const payload = {
      user_id: targetUserId,
      ...updatedFields,
      user_type: user_type,
    };
    // console.log(payload);
    const response = await dispatch(
      UpdateDetailsOfUsers(objectToFormData(payload))
    );
    if (response?.status === 200) {
      dispatch(throwSuccess(response?.message));
      setIsEditDetails(false);
      dispatch(setIsEditUserProfile(false));
      dispatch(setIsEditDataOfProfile(null));
    } else {
      dispatch(throwError(response));
    }
    setIsLoading(false);
  };
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        // Membership change is submitted on its own so that untouched or
        // incomplete company fields are not sent as profile updates.
        if (values.membership_plan_id !== initialValues.membership_plan_id) {
          handleUpdate({
            membership_plan_id: values.membership_plan_id,
            ...(values.membership_payment_proof
              ? {
                  membership_payment_proof: values.membership_payment_proof,
                  membership_payment_proof_name:
                    values.membership_payment_proof_name,
                }
              : {}),
          });
          return;
        }
        const changedFields = pickBy(
          values,
          (value, key) => !isEqual(value, initialValues[key])
        );
        // console.log(changedFields);
        handleUpdate(changedFields);
      }}
    >
      {(props) => {
        const {
          values,
          errors,
          handleChange,
          setFieldValue,
          handleSubmit,
          resetForm,
        } = props;

        return (
          <form>
            <div className="row">
              <div className="text-18-500 title-text cmb-25">
                Membership Details
              </div>
              <div className="col-md-6 cmb-22">
                <div className="text-14-400 color-black-olive cmb-10">
                  Current Membership
                </div>
                <div className="text-15-500 color-raisin-black">
                  {memberType || "-"}
                </div>
              </div>
              <div className="col-md-6 cmb-22">
                <Dropdown
                  label="Change Membership Type"
                  id="membership_plan_id"
                  placeholder="Select Member Type"
                  options={memberList}
                  value={values.membership_plan_id}
                  error={errors.membership_plan_id}
                  optionKey="id"
                  optionValue="name"
                  onChange={handleChange}
                />
                {values.membership_plan_id !==
                  initialValues.membership_plan_id && (
                  <div className="text-13-400 color-black-olive cmt-10">
                    Changing Membership Type will submit a membership change
                    request. Existing Company profile changes are not part of
                    this request.
                  </div>
                )}
              </div>
              {userType === "6" &&
                values.membership_plan_id !==
                  initialValues.membership_plan_id &&
                !["2", "11"].includes(values.membership_plan_id) && (
                  <>
                    {values?.membership_payment_proof ? (
                      <div className="col-md-6 cmb-24 d-flex justify-content-start">
                        <div className="d-flex align-items-center gap-3">
                          {/* Label */}
                          <div className="text-15-400 color-raisin-black">
                            Payment Proof:
                          </div>

                          {/* Download Button */}
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="text-nowrap text-truncate"
                              style={{ maxWidth: "200px" }}
                            >
                              {values?.membership_payment_proof?.startsWith(
                                "data:"
                              )
                                ? values?.membership_payment_proof_name
                                : values?.membership_payment_proof ||
                                  "No file uploaded"}
                            </div>

                            {/* Download Button */}
                            <button
                              type="button"
                              onClick={(e) => {
                                if (
                                  values?.membership_payment_proof?.startsWith(
                                    "data:"
                                  )
                                ) {
                                  const link = document.createElement("a");
                                  link.href = values.membership_payment_proof;
                                  link.download = `${values?.membership_payment_proof_name}`;
                                  link.click();
                                } else {
                                  handleFileDownload?.(
                                    e,
                                    values?.membership_payment_proof,
                                    paymentAbstractPath
                                  );
                                }
                              }}
                              className="p-0 border-0 bg-transparent d-flex align-items-center"
                            >
                              <img
                                src={icons.ArrowDownward}
                                alt="Download"
                                width="18"
                                height="18"
                              />
                            </button>

                            {/* View Button */}
                            <button
                              type="button"
                              className="p-0 border-0 bg-transparent d-flex align-items-center"
                              onClick={(e) => {
                                const base64Data =
                                  values?.membership_payment_proof;
                                if (base64Data?.startsWith("data:")) {
                                  // Convert base64 to blob and open in new tab
                                  const byteString = atob(
                                    base64Data.split(",")[1]
                                  );
                                  const mimeString = base64Data
                                    .split(",")[0]
                                    .split(":")[1]
                                    .split(";")[0];

                                  const ab = new ArrayBuffer(byteString.length);
                                  const ia = new Uint8Array(ab);
                                  for (let i = 0; i < byteString.length; i++) {
                                    ia[i] = byteString.charCodeAt(i);
                                  }

                                  const blob = new Blob([ab], {
                                    type: mimeString,
                                  });
                                  const blobUrl = URL.createObjectURL(blob);
                                  window.open(blobUrl, "_blank");
                                } else {
                                  handleViewPreview?.(
                                    values?.membership_payment_proof,
                                    paymentAbstractPath
                                  );
                                }
                              }}
                            >
                              <img
                                src={icons.blueEyeIcon}
                                alt="View"
                                width="18"
                                height="18"
                              />
                            </button>

                            {/* Delete Button */}
                            <button
                              type="button"
                              className="p-0 border-0 bg-transparent d-flex align-items-center"
                              onClick={() =>
                                setFieldValue("membership_payment_proof", "")
                              }
                            >
                              <i className="bi bi-trash text-danger"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="col-md-6 cmb-24">
                        <FileUpload
                          label="Payment Proof"
                          fileType="all"
                          fileText={getFilenameFromUrl(
                            values.eventLogoFileName
                          )}
                          id="membership_payment_proof"
                          onChange={(e) => {
                            const value = e.target.value;
                            const fileName = e.target.fileName;
                            setFieldValue("membership_payment_proof", value);
                            setFieldValue(
                              "membership_payment_proof_name",
                              fileName
                            );
                          }}
                          value={values.membership_payment_proof}
                          error={errors.membership_payment_proof}
                        />
                      </div>
                    )}
                  </>
                )}
              <div className="text-18-500 title-text cmb-25">
                Company Details
              </div>
              <div className="col-md-6 cmb-22">
                <TextInput
                  placeholder="Company Name*"
                  id="company_name"
                  value={values?.company_name}
                  onChange={(e) => {
                    setFieldValue(
                      "company_name",
                      titleCaseString(e.target.value)
                    );
                  }}
                  error={errors.company_name}
                />
              </div>
              <div className="col-md-6 cmb-22">
                <TextInput
                  placeholder="Company Email Id*"
                  id="company_email_id"
                  value={values?.company_email_id}
                  onChange={handleChange}
                  error={errors.company_email_id}
                />
              </div>
              <div className="col-md-6 cmb-22">
                <TextInput
                  placeholder="Company Contact Number*"
                  id="company_contact_number"
                  value={values?.company_contact_number}
                  onChange={handleChange}
                  error={errors.company_contact_number}
                />
              </div>

              <div className="col-md-6 cmb-22">
                <Location
                  type="country"
                  data={{
                    id: "company_country",
                    placeholder: "Country*",
                    value: values?.company_country,
                    error: errors.company_country,
                    onChange: (e) => {
                      setFieldValue("country_name", e?.target?.data?.country);
                      setFieldValue(
                        "company_country_name",
                        e?.target?.data?.country
                      );
                      handleChange(e);
                    },
                  }}
                />
              </div>
              <div className="col-md-6 cmb-22">
                <Location
                  type="state"
                  data={{
                    id: "company_state",
                    placeholder: "State/Province*",
                    value: values?.company_state,
                    error: errors.company_state,
                    countryId: values?.company_country,
                    disabled: !values?.company_country,
                    onChange: (e) => {
                      setFieldValue("state_name", e?.target?.data?.state);
                      setFieldValue(
                        "company_state_name",
                        e?.target?.data?.state
                      );

                      handleChange(e);
                    },
                  }}
                />
              </div>
              <div className="col-md-6 cmb-22">
                <CreatableCityDropDown
                  id="company_city"
                  disabled={!values?.company_state}
                  onChange={(e) => {
                    setFieldValue("company_city_name", e?.target?.data?.city);

                    handleChange(e);
                  }}
                  value={values?.company_city}
                  error={errors?.company_city}
                  countryId={values?.country_name}
                  stateId={values?.state_name}
                  isClearable
                />
              </div>
              {+values?.company_city === 586 && (
                <div className="col-md-6 cmb-22">
                  <TextInput
                    placeholder="Add Other City"
                    id="other_city"
                    value={values?.other_city}
                    error={errors.other_city}
                    onChange={handleChange}
                  />
                </div>
              )}

              <div className="col-md-6 cmb-22">
                <TextInput
                  placeholder="Strength of the Company"
                  id="strength_of_company"
                  value={values?.strength_of_company}
                  onChange={handleChange}
                  error={errors.strength_of_company}
                />
              </div>
              <div className="col-md-6 cmb-22">
                <TextInput
                  placeholder="No. of IFERP Premium Professional Members"
                  id="no_of_premium_professional_members"
                  value={values?.no_of_premium_professional_members}
                  onChange={handleChange}
                  error={errors.no_of_premium_professional_members}
                />
              </div>

              <div className="d-flex justify-content-center gap-4 mt-4">
                <Button
                  isRounded
                  text="Cancel"
                  btnStyle="light-outline"
                  className="cps-50 cpe-50"
                  onClick={() => {
                    resetForm();
                    setIsEditDetails(false);
                  }}
                />
                <Button
                  isRounded
                  text={
                    values.membership_plan_id !== initialValues.membership_plan_id
                      ? "Submit Change Request"
                      : "Submit"
                  }
                  btnStyle="primary-dark"
                  className="cps-40 cpe-40"
                  onClick={handleSubmit}
                  btnLoading={isLoading}
                  disabled={isEqual(initialValues, values)}
                />
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default EditCompanyDetails;
